'use client'

import React, { useCallback, useRef, useState } from 'react'

import { format } from 'date-fns'
import { HiMiniCheck, HiOutlineXMark } from 'react-icons/hi2'
import { LuCalendar } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import Calendar, { type DateWithMessage } from '@/components/common/Calendar'
import { parseSmartDateTimeInput } from '@/components/common/Daypicker/smartDateTime'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/common/Dialog'
import Popover from '@/components/common/PopoverV2'
import { TEXT_INPUT_STYLES } from '@/components/common/TextInput'
import TimePicker from '@/components/common/Timepicker'

interface Daypicker {
  date: Date | undefined
  onSelect: (date: Date | undefined) => void
  popoverClassname?: string
  buttonClassname?: string
  placeholder?: string
  datesWithMessages?: DateWithMessage[]
  showTime?: boolean
  hideClearButton?: boolean
  children?: React.ReactNode
}

interface DaypickerCalendarProps {
  date: Date | undefined
  datesWithMessages?: DateWithMessage[]
  showTime?: boolean
  calendarClassName?: string
  calendarClassNames?: React.ComponentProps<typeof Calendar>['classNames']
  timeClassName?: string
  onSelect: (date: Date | undefined) => void
  onCalendarSelect?: (date: Date | undefined) => void
}

const QUICK_TIME_OPTIONS = [
  { label: 'Now', kind: 'now' },
  { label: '9 AM', kind: 'time', hours: 9, minutes: 0 },
  { label: 'Noon', kind: 'time', hours: 12, minutes: 0 },
  { label: '3 PM', kind: 'time', hours: 15, minutes: 0 },
  { label: '5 PM', kind: 'time', hours: 17, minutes: 0 },
] as const

export const DaypickerCalendar: React.FC<DaypickerCalendarProps> = ({
  date,
  datesWithMessages,
  showTime = false,
  calendarClassName,
  calendarClassNames,
  timeClassName,
  onSelect,
  onCalendarSelect,
}) => {
  const handleSelect = useCallback(
    (newDate: Date | undefined) => {
      let selectedDate = newDate
      if (newDate && date) {
        const updatedDate = new Date(newDate)
        updatedDate.setHours(
          date.getHours(),
          date.getMinutes(),
          date.getSeconds(),
          date.getMilliseconds(),
        )
        selectedDate = updatedDate
      }

      onSelect(selectedDate)
      onCalendarSelect?.(selectedDate)
    },
    [date, onSelect, onCalendarSelect],
  )

  const handleTimeChange = useCallback(
    (newDate: Date | undefined) => {
      onSelect(newDate)
    },
    [onSelect],
  )

  return (
    <div className="flex flex-col">
      <Calendar
        mode="single"
        selected={date}
        onSelect={handleSelect}
        month={date}
        defaultMonth={date}
        initialFocus={false}
        showOutsideDays={false}
        datesWithMessages={datesWithMessages}
        className={calendarClassName}
        classNames={calendarClassNames}
      />
      {showTime && (
        <div
          className={twMerge(
            'flex items-center pb-4 pt-3 px-5 w-full border-t border-primary-200 dark:border-primary-700',
            timeClassName,
          )}
        >
          <TimePicker date={date} setDate={handleTimeChange} />
        </div>
      )}
    </div>
  )
}

const Daypicker: React.FC<Daypicker> = ({
  date,
  onSelect,
  popoverClassname = '',
  buttonClassname = '',
  placeholder = 'Select a date',
  datesWithMessages,
  showTime = false,
  hideClearButton = false,
  children,
}) => {
  const ref = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [draftDate, setDraftDate] = useState<Date | undefined>(date)
  const [parseBaseDate, setParseBaseDate] = useState<Date | undefined>(date)
  const [smartInput, setSmartInput] = useState<string>('')
  const [smartParsedDate, setSmartParsedDate] = useState<Date | null>(null)

  const selectedDate = showTime ? draftDate : date

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setDraftDate(date)
        setParseBaseDate(date ?? new Date())
        setSmartInput('')
        setSmartParsedDate(null)
      }
      setOpen(nextOpen)
    },
    [date],
  )

  const handleSelect = useCallback(
    (newDate: Date | undefined) => {
      if (showTime) {
        setDraftDate(newDate)
        setParseBaseDate(newDate ?? new Date())
        setSmartInput('')
        setSmartParsedDate(null)
        return
      }
      onSelect(newDate)
    },
    [onSelect, showTime],
  )

  const handleCalendarSelect = useCallback(
    (_newDate: Date | undefined) => {
      if (!showTime) {
        setOpen(false)
      }
    },
    [showTime],
  )

  const handleCancel = useCallback(() => {
    setDraftDate(date)
    setParseBaseDate(date)
    setSmartInput('')
    setSmartParsedDate(null)
    setOpen(false)
  }, [date])

  const handleApply = useCallback(() => {
    onSelect(draftDate)
    setOpen(false)
  }, [draftDate, onSelect])

  const handleSmartInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value
      setSmartInput(value)

      const parsedDate = parseSmartDateTimeInput(value, {
        baseDate: parseBaseDate ?? date ?? new Date(),
      })
      setSmartParsedDate(parsedDate)
      if (parsedDate) {
        setDraftDate(parsedDate)
      }
    },
    [date, parseBaseDate],
  )

  const handleSmartInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter' || !smartParsedDate) {
        return
      }

      event.preventDefault()
      handleApply()
    },
    [handleApply, smartParsedDate],
  )

  const handleQuickTimeClick = useCallback(
    (option: (typeof QUICK_TIME_OPTIONS)[number]) => {
      const nextDate =
        option.kind === 'now' ? new Date() : new Date(draftDate ?? new Date())

      if (option.kind === 'time') {
        nextDate.setHours(option.hours, option.minutes, 0, 0)
      }

      setDraftDate(nextDate)
      setParseBaseDate(nextDate)
      setSmartInput('')
      setSmartParsedDate(null)
    },
    [draftDate],
  )

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      onSelect(undefined)
    },
    [onSelect],
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        ref={ref}
        className={twMerge(
          children ? '' : `flex flex-grow w-full ${TEXT_INPUT_STYLES} px-3`,
          buttonClassname,
        )}
      >
        {children ? (
          children
        ) : (
          <>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center flex-grow w-full gap-x-2 text-sm">
                <div>
                  <LuCalendar
                    size={16}
                    className="flex-shrink-0 text-primary-500 dark:text-primary-400"
                  />
                </div>
                <div className={twMerge(date === undefined ? '' : 'truncate')}>
                  {date ? (
                    showTime ? (
                      format(date, 'LLL do, y h:mm a')
                    ) : (
                      format(date, 'LLL do, y')
                    )
                  ) : (
                    <span className="text-sm text-primary-400 dark:text-primary-500">
                      {placeholder}
                    </span>
                  )}
                </div>
              </div>
              {date !== undefined && !hideClearButton && (
                <div onClick={handleClear}>
                  <HiOutlineXMark className="h-4 w-4 text-primary-700 dark:text-primary-300 hover:opacity-70" />
                </div>
              )}
            </div>
          </>
        )}
      </Popover.Trigger>
      <Popover.Content
        align="start"
        className={twMerge(
          // 'flex flex-col items-start !w-fit my-2 z-[1] shadow-sm rounded-md bg-white dark:bg-primary-900 border border-primary-200 dark:border-primary-700',
          'flex max-h-[min(640px,calc(var(--radix-popover-content-available-height)-8px))] flex-col overflow-hidden p-0',
          popoverClassname,
        )}
      >
        <div className="min-h-0 overflow-y-auto">
          {showTime && (
            <div className="border-b border-primary-200 p-2 dark:border-primary-700">
              <input
                value={smartInput}
                onChange={handleSmartInputChange}
                onKeyDown={handleSmartInputKeyDown}
                className="h-8 w-full rounded-md border border-primary-200 bg-white px-2 text-sm text-primary-900 outline-none transition-[border-color,box-shadow] placeholder:text-primary-400 focus:border-accent-400 focus:ring-2 focus:ring-accent-400 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-100 dark:placeholder:text-primary-500 dark:focus:border-accent-400/50 dark:focus:ring-accent-400/50"
                placeholder="e.g. tomorrow 3pm"
              />
              {smartInput && (
                <div className="mt-1 text-xs text-primary-500 dark:text-primary-400">
                  {smartParsedDate
                    ? format(smartParsedDate, 'EEE, MMM d h:mm a')
                    : 'No date recognized'}
                </div>
              )}
            </div>
          )}
          <DaypickerCalendar
            date={selectedDate}
            onSelect={handleSelect}
            onCalendarSelect={handleCalendarSelect}
            datesWithMessages={datesWithMessages}
            showTime={false}
          />
          {showTime && (
            <div className="border-t border-primary-200 px-2 py-2 dark:border-primary-700">
              <div className="mb-2 flex flex-wrap gap-1.5">
                {QUICK_TIME_OPTIONS.map((option) => (
                  <Button
                    key={option.label}
                    type="button"
                    variant="outline-compact"
                    className="h-6 rounded-full px-2 text-xs font-normal"
                    onClick={() => handleQuickTimeClick(option)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              <TimePicker date={selectedDate} setDate={handleSelect} />
            </div>
          )}
        </div>
        {showTime && (
          <div className="flex shrink-0 items-center gap-2 border-t border-primary-200 bg-white px-2 py-2 dark:border-primary-700 dark:bg-primary-900">
            <Button
              type="button"
              variant="outline-compact"
              className="flex-1"
              onClick={handleCancel}
            >
              <HiOutlineXMark className="h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary-compact"
              className="flex-1"
              onClick={handleApply}
            >
              <HiMiniCheck className="h-3.5 w-3.5" />
              Apply
            </Button>
          </div>
        )}
      </Popover.Content>
    </Popover>
  )
}

export interface DayPickerDialogProps {
  initialValue?: Date | null
  datesWithMessages?: DateWithMessage[]
  open: boolean
  allowNull?: boolean
  onChange: (value: Date | null) => void
  setOpen: (open: boolean) => void
}

export const DayPickerDialog: React.FC<DayPickerDialogProps> = ({
  initialValue,
  datesWithMessages,
  open,
  allowNull = true,
  onChange,
  setOpen,
}) => {
  const [selected, setSelected] = React.useState<Date | null>(
    initialValue ?? null,
  )

  const disabled = !allowNull && selected === null
  const onOpenChange = (open: boolean) => {
    setOpen(open)
    if (!open) {
      setSelected(initialValue ?? null)
    }
  }

  const handleChange = (date: Date | undefined) => {
    // onChange(date ? date : null)
    setSelected(date ? date : null)
  }

  const handleSubmit = () => {
    onChange(selected)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        closeButtonClassName="top-2 right-2"
        // overlay className is used to prevent shift of the dialog content
        // when the number of calendar rows changes (e.g. a month with more days)
        className="pt-3 pb-0 px-2 mt-12"
        overlayClassName="flex items-start justify-center"
      >
        <DialogTitle className="text-md font-medium pl-2">
          Select date
        </DialogTitle>
        <div className="flex items-center justify-center -mx-2">
          <DaypickerCalendar
            date={selected ? selected : undefined}
            onSelect={handleChange}
            datesWithMessages={datesWithMessages}
            showTime={true}
          />
        </div>
        <DialogFooter className="py-3 -mx-2 px-3 border-t border-primary-200 dark:border-primary-800">
          <Button
            onClick={handleSubmit}
            className="w-full font-normal"
            variant="primary-compact"
            type="button"
            disabled={disabled}
          >
            {!disabled ? (
              <HiMiniCheck className="flex-shrink-0 h-[14px] w-[14px]" />
            ) : null}
            {selected === null ? (
              initialValue ? (
                'Clear'
              ) : disabled ? (
                'No date selected'
              ) : (
                'Clear'
              )
            ) : (
              <span>
                Select&nbsp;
                {format(selected, 'LLL d, yyyy h:mm a')}
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default Daypicker
